import React from "react";
import { ChevronLeft, ChevronRight, Move, PlayCircle, Trash, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import PageTutorial from "@/components/tutorial/PageTutorial";
import { useHaptics } from "@/haptics/HapticsProvider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const POS_KEY = "protocolResumeWidget.pos";
const EDGE_GAP = 16;
const WIDGET_SIZE = 48;
const EDGE_SNAP_ZONE = 56;
const TRASH_TARGET_SIZE = 64;
const TRASH_TARGET_BOTTOM = 24;
const WIDGET_SHOW_DELAY_MS = 720;
const TRASH_ANIMATION_DELAY_MS = 90;

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

export default function ProtocolResumeWidget() {
  const { light: hapticLight } = useHaptics();
  const {
    isOpen,
    hasActiveProtocol,
    resumeProtocolSession,
    setIsWidgetDragging,
    minimizeToken,
    clearProtocolSession,
  } = useProtocolShell();
  const [phase, setPhase] = React.useState("hidden");
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.side || typeof parsed.y !== "number") return null;
      return { side: parsed.side === "left" ? "left" : "right", y: parsed.y };
    } catch {
      return null;
    }
  });
  const wakeCollapseTimerRef = React.useRef(null);
  const showDelayTimerRef = React.useRef(null);
  const prevIsOpenRef = React.useRef(isOpen);
  const longPressTimerRef = React.useRef(null);
  const dragStartRef = React.useRef(null);
  const latestPointerRef = React.useRef(null);
  const dragOriginPosRef = React.useRef(null);
  const suppressClickRef = React.useRef(false);
  const lastDropZoneRef = React.useRef("none");
  const [dragPos, setDragPos] = React.useState(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false);
  const [showTrashTarget, setShowTrashTarget] = React.useState(false);
  const [isLargeScreen, setIsLargeScreen] = React.useState(false);

  const getXFromPosition = React.useCallback((pos) => {
    if (!pos || typeof window === "undefined") return window.innerWidth - WIDGET_SIZE - EDGE_GAP;
    return pos.side === "left" ? EDGE_GAP : window.innerWidth - WIDGET_SIZE - EDGE_GAP;
  }, []);

  React.useEffect(() => {
    if (position) return;
    if (typeof window === "undefined") return;
    const defaultY = clamp(window.innerHeight - 150, 88, window.innerHeight - 130);
    setPosition({ side: "right", y: defaultY });
  }, [position]);

  React.useEffect(() => {
    if (!position) return;
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(position));
    } catch {
      // Ignore storage failures.
    }
  }, [position]);

  React.useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen || !hasActiveProtocol) {
      if (showDelayTimerRef.current) {
        clearTimeout(showDelayTimerRef.current);
        showDelayTimerRef.current = null;
      }
      setPhase("hidden");
      return;
    }

    // Nyss stängd shell (öppen -> stängd): låt minimize-effekten styra delay,
    // annars risk för "blink" (collapsed -> hidden -> collapsed).
    if (wasOpen && !isOpen) return;

    // Vid första render med aktiv session: visa direkt.
    if (phase === "hidden") {
      setPhase("collapsed");
    }
  }, [isOpen, hasActiveProtocol, phase]);

  React.useEffect(() => {
    // Kör delay uttryckligen när shell minimeras.
    if (!hasActiveProtocol || isOpen) return;
    if (!minimizeToken) return;

    if (showDelayTimerRef.current) {
      clearTimeout(showDelayTimerRef.current);
      showDelayTimerRef.current = null;
    }

    setPhase("hidden");
    showDelayTimerRef.current = setTimeout(() => {
      setPhase("collapsed");
      showDelayTimerRef.current = null;
    }, WIDGET_SHOW_DELAY_MS);
  }, [minimizeToken, hasActiveProtocol, isOpen]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsLargeScreen(media.matches);
    update();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  React.useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (wakeCollapseTimerRef.current) clearTimeout(wakeCollapseTimerRef.current);
      if (showDelayTimerRef.current) clearTimeout(showDelayTimerRef.current);
      setIsWidgetDragging(false);
    };
  }, [setIsWidgetDragging]);

  React.useEffect(() => {
    setIsWidgetDragging(isDragging);
    return () => setIsWidgetDragging(false);
  }, [isDragging, setIsWidgetDragging]);

  React.useEffect(() => {
    if (!isDragging) {
      setShowTrashTarget(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowTrashTarget(true);
    }, TRASH_ANIMATION_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isDragging]);

  const isExpanded = phase === "expanded";
  const isCollapsed = phase === "collapsed";
  const visible = isExpanded || isCollapsed;
  const showExpandedOverlay = isExpanded && !isDragging;
  const dragX = dragPos?.x ?? getXFromPosition(position);
  const nearLeftDrop = isDragging && dragX <= EDGE_SNAP_ZONE;
  const nearRightDrop =
    isDragging &&
    typeof window !== "undefined" &&
    dragX >= window.innerWidth - WIDGET_SIZE - EDGE_SNAP_ZONE;
  const nearAnyDrop = nearLeftDrop || nearRightDrop;
  const nearTrashDrop = React.useMemo(() => {
    if (!isDragging || !dragPos || typeof window === "undefined") return false;
    const targetCenterX = window.innerWidth / 2;
    const targetCenterY = window.innerHeight - TRASH_TARGET_BOTTOM - TRASH_TARGET_SIZE / 2;
    const widgetCenterX = dragPos.x + WIDGET_SIZE / 2;
    const widgetCenterY = dragPos.y + WIDGET_SIZE / 2;
    const distance = Math.hypot(widgetCenterX - targetCenterX, widgetCenterY - targetCenterY);
    return distance <= TRASH_TARGET_SIZE * 0.72;
  }, [isDragging, dragPos]);

  React.useEffect(() => {
    const currentZone = nearTrashDrop ? "trash" : nearLeftDrop ? "left" : nearRightDrop ? "right" : "none";
    const previousZone = lastDropZoneRef.current;

    if (isDragging && currentZone !== "none" && currentZone !== previousZone) {
      hapticLight();
    }

    lastDropZoneRef.current = currentZone;
  }, [hapticLight, isDragging, nearLeftDrop, nearRightDrop, nearTrashDrop]);

  const handlePointerDown = (event) => {
    if (!isCollapsed) return;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    latestPointerRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    suppressClickRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      setIsDragging(true);
      dragOriginPosRef.current = position;
      const p = latestPointerRef.current;
      if (p) {
        const minX = EDGE_GAP;
        const maxX = window.innerWidth - WIDGET_SIZE - EDGE_GAP;
        const minY = 88;
        const maxY = window.innerHeight - WIDGET_SIZE - 8;
        const nextX = clamp(p.x - WIDGET_SIZE / 2, minX, maxX);
        const nextY = clamp(p.y - 24, minY, maxY);
        setDragPos({ x: nextX, y: nextY });
      }
    }, 260);
  };

  const handlePointerMove = (event) => {
    if (!isCollapsed) return;
    if (!dragStartRef.current) return;
    latestPointerRef.current = {
      x: event.clientX,
      y: event.clientY,
    };

    if (!isDragging) return;

    const minX = EDGE_GAP;
    const maxX = window.innerWidth - WIDGET_SIZE - EDGE_GAP;
    const minY = 88;
    const maxY = window.innerHeight - WIDGET_SIZE - 8;
    const nextX = clamp(event.clientX - WIDGET_SIZE / 2, minX, maxX);
    const nextY = clamp(event.clientY - 24, minY, maxY);
    setDragPos({ x: nextX, y: nextY });
    suppressClickRef.current = true;
  };

  const handlePointerUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (isDragging) {
      const current = dragPos;
      const original = dragOriginPosRef.current;
      if (current && typeof window !== "undefined") {
        if (nearTrashDrop) {
          setConfirmCloseOpen(true);
        } else {
        const nearLeft = current.x <= EDGE_SNAP_ZONE;
        const nearRight = current.x >= window.innerWidth - WIDGET_SIZE - EDGE_SNAP_ZONE;

        if (nearLeft || nearRight) {
          setPosition({
            side: nearLeft ? "left" : "right",
            y: current.y,
          });
        } else if (original) {
          setPosition(original);
        }
        }
      }

      setIsDragging(false);
      setDragPos(null);
      dragOriginPosRef.current = null;
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 120);
    }
    dragStartRef.current = null;
    latestPointerRef.current = null;
  };

  const handleWidgetClick = () => {
    if (suppressClickRef.current) return;
    if (isCollapsed) {
      setPhase("expanded");
      if (wakeCollapseTimerRef.current) {
        clearTimeout(wakeCollapseTimerRef.current);
      }
      wakeCollapseTimerRef.current = setTimeout(() => {
        setPhase("collapsed");
        wakeCollapseTimerRef.current = null;
      }, 2600);
      return;
    }
    resumeProtocolSession();
  };

  const collapseExpandedWidget = React.useCallback(() => {
    if (!isExpanded) return;
    if (wakeCollapseTimerRef.current) {
      clearTimeout(wakeCollapseTimerRef.current);
      wakeCollapseTimerRef.current = null;
    }
    setPhase("collapsed");
  }, [isExpanded]);

  const handleConfirmClose = React.useCallback(() => {
    setConfirmCloseOpen(false);
    clearProtocolSession();
  }, [clearProtocolSession]);

  if (!hasActiveProtocol || isOpen) return null;

  return (
    <>
      {isLargeScreen ? (
        <>
          <div className="native-v3-protocol-widget-desktop fixed bottom-6 right-6 z-[85] hidden items-center gap-2 md:flex">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setConfirmCloseOpen(true)}
              className="native-v3-protocol-widget-close h-12 w-12 border-border/80 bg-background/95 shadow-lg backdrop-blur"
              aria-label="Stäng protokoll"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              onClick={resumeProtocolSession}
              className="native-v3-protocol-widget-resume h-12 border border-primary/60 bg-primary px-4 text-primary-foreground shadow-[0_10px_28px_-10px_hsl(var(--primary))] hover:bg-primary/95"
            >
              <PlayCircle className="mr-2 h-5 w-5 text-white" />
              <span className="font-medium">Återuppta protokoll</span>
            </Button>
          </div>

          <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
            <AlertDialogContent className="native-v3-protocol-alert">
              <AlertDialogHeader>
                <AlertDialogTitle>Stäng protokoll-läge?</AlertDialogTitle>
                <AlertDialogDescription>
                  Pågående protokoll stängs. Matchen finns kvar i dina skapade protokoll för att
                  återuppta senare.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="native-v3-protocol-alert-footer">
                <AlertDialogCancel className="native-v3-protocol-alert-cancel">Avbryt</AlertDialogCancel>
                <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={handleConfirmClose}>
                  Stäng protokoll
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : (
        <>
      {visible ? <PageTutorial pageName="protocolWidget" autoStart={visible && !isDragging} /> : null}
      <style>{`
        @keyframes widgetTrashRise {
          0% { transform: translateY(22px) scale(0.92); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
      {showExpandedOverlay ? (
        <button
          type="button"
          aria-label="Stäng widget"
          onClick={collapseExpandedWidget}
          className="fixed inset-0 z-[83] bg-black/60 transition-opacity duration-300 sm:hidden"
        />
      ) : null}
      {isDragging ? (
        <div className="native-v3-protocol-drag-mode pointer-events-none fixed inset-0 z-[84] sm:hidden">
          <div className="native-v3-protocol-drag-backdrop absolute inset-0 transition-opacity duration-200" />
          <div className="native-v3-protocol-drag-heading absolute inset-x-0 flex justify-center px-4">
            <div className="native-v3-protocol-widget-hint flex items-center gap-3 border border-white/20 bg-black/70 px-3 py-2 text-white/95">
              <Move className="h-4 w-4 shrink-0" />
              <span className="flex flex-col">
                <strong>Flytta protokollet</strong>
                <small>Dra till en kant för att fästa widgeten</small>
              </span>
            </div>
          </div>
          <div className="native-v3-protocol-dock-target native-v3-protocol-dock-target-left" data-active={nearLeftDrop}>
            <ChevronLeft className="h-5 w-5" />
            <span>Fäst vänster</span>
          </div>
          <div className="native-v3-protocol-dock-target native-v3-protocol-dock-target-right" data-active={nearRightDrop}>
            <span>Fäst höger</span>
            <ChevronRight className="h-5 w-5" />
          </div>
          <div className="absolute inset-x-0 bottom-6 flex justify-center">
            <div
              className={[
                "native-v3-protocol-widget-trash relative flex h-14 w-14 items-center justify-center bg-primary text-white shadow-xl transition-all duration-300 ease-out",
                showTrashTarget
                  ? "translate-y-0 opacity-100 animate-[widgetTrashRise_240ms_cubic-bezier(0.22,1,0.36,1)]"
                  : "translate-y-6 opacity-0",
                nearTrashDrop
                  ? "scale-110 bg-[#a11e25] ring-2 ring-white/70 shadow-[0_12px_30px_rgba(161,30,37,0.45)]"
                  : "scale-100 hover:bg-[#a11e25]",
              ].join(" ")}
            >
              {nearTrashDrop ? <Trash className="h-7 w-7" /> : <Trash2 className="h-7 w-7" />}
            </div>
          </div>
        </div>
      ) : null}
      <div
        id="protocolResumeWidget"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={[
          "native-v3-protocol-widget fixed z-[85] select-none sm:hidden",
          isDragging ? "transition-none" : "transition-all duration-300 ease-out",
          visible ? "translate-x-0 opacity-100" : "translate-x-16 opacity-0 pointer-events-none",
        ].join(" ")}
        style={{
          top: `${(isDragging ? dragPos?.y : position?.y) ?? 120}px`,
          left: isDragging
            ? `${dragPos?.x ?? getXFromPosition(position)}px`
            : position?.side === "left"
              ? `${EDGE_GAP}px`
              : "auto",
          right: isDragging
            ? "auto"
            : position?.side === "right"
              ? `${EDGE_GAP}px`
              : "auto",
          touchAction: isCollapsed ? "none" : "auto",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",
        }}
      >
        <div className="native-v3-protocol-widget-frame relative">
          {isDragging ? (
            <span
              className={[
                "pointer-events-none absolute inset-0 ring-2 animate-ping",
                nearAnyDrop || nearTrashDrop
                  ? "ring-slate-700/80 dark:ring-sky-300/80"
                  : "ring-slate-700/45 dark:ring-sky-300/50",
              ].join(" ")}
            />
          ) : null}
          <Button
            type="button"
            onClick={handleWidgetClick}
            draggable={false}
            className={[
              "native-v3-protocol-widget-button h-12 overflow-hidden border border-primary/60 bg-primary text-primary-foreground shadow-[0_10px_28px_-10px_hsl(var(--primary))] transition-[width,padding,border-radius,transform,box-shadow,background-color] duration-650 ease-in-out hover:bg-primary/95",
              isExpanded
                ? "w-[210px] px-4"
                : "w-12 px-0 gap-0 justify-center",
              isDragging
                ? nearAnyDrop
                  ? "scale-115 shadow-2xl ring-2 ring-primary/75"
                  : "scale-110 shadow-2xl ring-2 ring-primary/45"
                : "scale-100 ring-1 ring-primary/35",
            ].join(" ")}
            aria-label="Återuppta protokoll"
          >
            <PlayCircle className={isExpanded ? "mr-2 h-5 w-5 text-white" : "h-6 w-6 text-white"} />
            <span
              className={[
                "transition-all duration-200 whitespace-nowrap",
                isExpanded ? "max-w-[180px] opacity-100" : "max-w-0 opacity-0 overflow-hidden",
              ].join(" ")}
            >
              Pågående protokoll
            </span>
          </Button>
        </div>
      </div>
      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent className="native-v3-protocol-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>Stäng protokoll-läge?</AlertDialogTitle>
            <AlertDialogDescription>
              Pågående protokoll stängs. Matchen finns kvar i dina skapade protokoll för att
              återuppta senare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="native-v3-protocol-alert-footer">
            <AlertDialogCancel className="native-v3-protocol-alert-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={handleConfirmClose}>
              Stäng protokoll
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </>
      )}
    </>
  );
}
